import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { FileWarning, Loader2, MailWarning, Sparkles } from "lucide-react";
import type { ReportRange } from "@ticketnjoy/types";

import { DashboardShell } from "@/components/dashboard-shell";
import { QueryError } from "@/components/query-error";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EditorHome } from "@/features/home/editor-home";
import { useAuthenticatedMe } from "@/features/organizers/api";
import { useDashboardQuery } from "@/features/reports/api";
import {
  KpiCards,
  OccupancyCard,
  PaymentBreakdownCard,
  RecentOrdersCard,
  RefundAndCustomersCard,
  TopEventsCard,
} from "@/features/reports/components/dashboard-cards";
import { TrendChart } from "@/features/reports/components/trend-chart";
import {
  formatIsoRange,
  istanbulTodayIso,
  REPORT_RANGES,
  useReportRange,
} from "@/features/reports/range";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { isOnboarded } from "@/pages/onboarding";

export function DashboardPage() {
  const me = useAuthenticatedMe();
  const t = useT();
  const fmt = useFormat();
  const period = useReportRange("week");
  const [shopId, setShopId] = useState<string | null>(null);
  const role = me.data?.role;
  const canSeeSales = role === "Owner" || role === "Manager" || role === "Accountant";
  const dashboard = useDashboardQuery(period.query, shopId, canSeeSales && period.valid);
  const rangeLabel =
    period.range === "custom"
      ? formatIsoRange(fmt, period.from, period.to)
      : t(`range.${period.range}`);

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!me.data) {
    return null;
  }

  if (me.data.role === "Gate") {
    return <Navigate to="/check-in" replace />;
  }

  const { data: meData } = me;
  const shops = meData.shops;

  if (!canSeeSales) {
    return (
      <DashboardShell companyName={meData.companyName} role={meData.role}>
        <EditorHome companyName={meData.companyName} />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell companyName={meData.companyName} role={meData.role}>
      {/* Onboarding tamamlanmadıysa — ince hatırlatma. */}
      {meData.emailVerified && !isOnboarded() && (
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <Sparkles className="mt-0.5 h-5 w-5 text-primary" />
            <div className="flex-1 space-y-3">
              <div>
                <CardTitle className="text-base">{t("pano.onboarding.title")}</CardTitle>
                <CardDescription>{t("pano.onboarding.description")}</CardDescription>
              </div>
              <Button size="sm" asChild>
                <Link to="/onboarding">{t("pano.onboarding.cta")}</Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      {!meData.emailVerified && (
        <Card className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <MailWarning className="mt-0.5 h-5 w-5 text-amber-600" />
            <div>
              <CardTitle className="text-base">
                {t("emailVerification.title")}
              </CardTitle>
              <CardDescription>
                {t("emailVerification.description")}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      {!meData.isLegalInfoComplete && (
        <Card className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <FileWarning className="mt-0.5 h-5 w-5 text-amber-600" />
            <div className="flex-1 space-y-3">
              <div>
                <CardTitle className="text-base">
                  {t("legalBanner.title")}
                </CardTitle>
                <CardDescription>
                  {t("legalBanner.description")}
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" asChild>
                <Link to="/settings">{t("legalBanner.cta")}</Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Header — başlık + filtre kontrolleri */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("pano.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {meData.companyName} • {rangeLabel}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {shops.length > 1 ? (
            <Select
              value={shopId ?? "all"}
              onValueChange={(v) => setShopId(v === "all" ? null : v)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pano.allShops")}</SelectItem>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select
            value={period.range}
            onValueChange={(v) => period.setRange(v as ReportRange)}
          >
            <SelectTrigger className="w-[150px]" aria-label={t("reports.rangeLabel")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_RANGES.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(`range.${r}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {period.range === "custom" ? (
            <>
              <Input
                type="date"
                className="w-[150px]"
                aria-label={t("reports.from")}
                value={period.from}
                max={period.to || istanbulTodayIso()}
                onChange={(e) => period.setFrom(e.target.value)}
              />
              <Input
                type="date"
                className="w-[150px]"
                aria-label={t("reports.to")}
                value={period.to}
                min={period.from}
                max={istanbulTodayIso()}
                onChange={(e) => period.setTo(e.target.value)}
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Hata / yükleme / boş içerik */}
      {dashboard.isError ? (
        <QueryError
          message={t("pano.loadError")}
          detail={dashboard.error?.detail}
          onRetry={() => dashboard.refetch()}
          retrying={dashboard.isFetching}
        />
      ) : dashboard.isLoading || !dashboard.data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="h-[110px] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <KpiCards kpis={dashboard.data.kpis} currency={dashboard.data.currency} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t(`pano.trendTitle.${period.range}`)}</CardTitle>
              <CardDescription>
                {t("pano.trendSummary", {
                  orders: fmt.number(dashboard.data.kpis.ordersPaid),
                  tickets: fmt.number(dashboard.data.kpis.ticketsSold),
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TrendChart
                points={dashboard.data.trend}
                bucket={dashboard.data.trendBucket}
                currency={dashboard.data.currency}
              />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <TopEventsCard
              events={dashboard.data.topEvents}
              currency={dashboard.data.currency}
            />
            <OccupancyCard items={dashboard.data.occupancy} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <PaymentBreakdownCard
              items={dashboard.data.paymentMethods}
              currency={dashboard.data.currency}
            />
            <RefundAndCustomersCard
              refunds={dashboard.data.refunds}
              customers={dashboard.data.customers}
              currency={dashboard.data.currency}
            />
          </div>

          <RecentOrdersCard
            orders={dashboard.data.recentOrders}
            currency={dashboard.data.currency}
          />
        </div>
      )}
    </DashboardShell>
  );
}
